import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Users, RefreshCw, Trophy } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { tournamentStandingsAPI, tournamentTeamGroupsAPI } from '../services/tournamentApi';
import { btn } from '../../utils/buttonStyles';
import { useCountUp } from '../../hooks/useCountUp';

/**
 * Aggregates two groups' standings and decides an overall winner: total
 * points first, then total point differential (F-A), then total head-to-head
 * wins, else declares a tie (all three can legitimately land on 0-0 when
 * neither group has played yet). Always returns both groups' identity in a
 * stable left/right order (matching the input array) plus which metric
 * ultimately decided it, so the UI can render a fixed left-vs-right layout
 * regardless of who's winning. Returns null when there aren't exactly two
 * groups with matching standings data.
 */
function computeGroupBattle(teamGroups, standings) {
  if (!Array.isArray(teamGroups) || teamGroups.length !== 2) return null;
  const [groupA, groupB] = teamGroups;
  if (!groupA.teamIds?.length || !groupB.teamIds?.length) return null;

  const aggregate = (group) => {
    const teamIdSet = new Set(group.teamIds.map(String));
    return standings.reduce((acc, s) => {
      if (!teamIdSet.has(String(s.participantId))) return acc;
      acc.points += s.points;
      acc.pointsFor += s.pointsFor;
      acc.pointsAgainst += s.pointsAgainst;
      acc.wins += s.wins;
      return acc;
    }, { points: 0, pointsFor: 0, pointsAgainst: 0, wins: 0 });
  };

  const a = aggregate(groupA);
  const b = aggregate(groupB);

  const tiers = [
    { a: a.points, b: b.points, metricLabel: 'points', marginSuffix: '' },
    { a: a.pointsFor - a.pointsAgainst, b: b.pointsFor - b.pointsAgainst, metricLabel: 'point differential', marginSuffix: ' pt diff' },
    { a: a.wins, b: b.wins, metricLabel: 'head-to-head wins', marginSuffix: ' wins' },
  ];

  const base = {
    groupAName: groupA.groupName,
    groupBName: groupB.groupName,
    groupAPoints: a.points,
    groupBPoints: b.points,
  };

  for (const t of tiers) {
    if (t.a === t.b) continue;
    return {
      ...base,
      tied: false,
      winnerIsA: t.a > t.b,
      groupAValue: t.a,
      groupBValue: t.b,
      margin: Math.abs(t.a - t.b),
      marginSuffix: t.marginSuffix,
      metricLabel: t.metricLabel,
    };
  }

  return { ...base, tied: true, groupAValue: a.points, groupBValue: b.points };
}

/**
 * Reveal banner for the group-vs-group result. Its count-up hooks only run
 * while `active` is true, so switching into the Standings tab (activeTab
 * transitioning to 'standings') always replays the animation from 0 — the
 * tab itself never unmounts between visits, so this is the only reset signal.
 */
const FLOWER_EMOJI = ['🌸', '🌺', '🌼', '🌻', '💐'];

/**
 * Full-viewport petal fall, portaled to document.body so it renders above
 * everything regardless of where the banner sits in the page (and isn't
 * clipped by any ancestor's overflow). Petals start above the screen and
 * fall past the bottom with some horizontal sway, like petals being thrown
 * in the air.
 */
function FlowerOverlay({ burstKey }) {
  const particles = useMemo(() => {
    return Array.from({ length: 28 }, (_, i) => ({
      id: i,
      emoji: FLOWER_EMOJI[Math.floor(Math.random() * FLOWER_EMOJI.length)],
      left: Math.random() * 100,
      delay: Math.random() * 600,
      duration: 2600 + Math.random() * 1800,
      sway: (Math.random() - 0.5) * 160,
      rotate: (Math.random() - 0.5) * 720,
      size: 18 + Math.random() * 16,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [burstKey]);

  return createPortal(
    <div className="flower-overlay" aria-hidden="true">
      {particles.map(p => (
        <span
          key={`${burstKey}-${p.id}`}
          className="flower-particle"
          style={{
            left: `${p.left}%`,
            fontSize: `${p.size}px`,
            animationDelay: `${p.delay}ms`,
            animationDuration: `${p.duration}ms`,
            '--sway': `${p.sway}px`,
            '--rotate': `${p.rotate}deg`,
          }}
        >
          {p.emoji}
        </span>
      ))}
      <style>{`
        .flower-overlay {
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 9999;
          overflow: hidden;
        }
        @keyframes flowerFall {
          0% { transform: translateY(-10vh) translateX(0) rotate(0deg); opacity: 0; }
          8% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(110vh) translateX(var(--sway)) rotate(var(--rotate)); opacity: 0; }
        }
        .flower-particle {
          position: absolute;
          top: 0;
          animation-name: flowerFall;
          animation-timing-function: ease-in;
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>,
    document.body
  );
}

// Short label identifying which metric a box's number represents, so a
// negative point-differential value (e.g. "-38") reads as "F-A: -38"
// rather than looking like a raw, broken team score.
const METRIC_ABBR = { points: 'PTS', 'point differential': 'F-A', 'head-to-head wins': 'WINS' };

function GroupBattleBanner({ groupBattle, active, theme }) {
  const { tied, winnerIsA, groupAName, groupBName, groupAPoints, groupBPoints, groupAValue, groupBValue, margin, marginSuffix, metricLabel } = groupBattle;

  // "X wins by N (X-Y)" reads left-to-right, so the winner always sits on
  // the left and the loser on the right — not a fixed groupA/groupB order.
  // Each box shows whichever metric actually decided the tiebreak (points,
  // point differential, or head-to-head wins) — signed and clearly labeled
  // so a negative point-differential value is unambiguous — and the center
  // just states the resulting difference.
  const leftName = tied || winnerIsA ? groupAName : groupBName;
  const rightName = tied || winnerIsA ? groupBName : groupAName;
  const leftValue = tied ? groupAPoints : (winnerIsA ? groupAValue : groupBValue);
  const rightValue = tied ? groupBPoints : (winnerIsA ? groupBValue : groupAValue);
  const metricAbbr = METRIC_ABBR[tied ? 'points' : metricLabel];
  const isSigned = !tied && metricLabel === 'point differential';
  const formatValue = (v) => (isSigned ? `${v >= 0 ? '+' : ''}${v}` : v);

  const leftCount = useCountUp(leftValue, 1200, active);
  const rightCount = useCountUp(rightValue, 1200, active);

  // Replay the petal fall every time the banner reveals (active flips
  // false->true) — same "visit the tab again -> replay" contract as the
  // count-up above. A fresh burstKey on each activation forces FlowerOverlay
  // to remount its particles with freshly randomized positions/timing.
  const [burstKey, setBurstKey] = useState(0);
  useEffect(() => {
    if (active) setBurstKey(k => k + 1);
  }, [active]);

  return (
    <div className={`group-battle-banner relative rounded-2xl border-2 border-yellow-500/40 bg-gradient-to-br from-yellow-500/10 via-transparent to-yellow-500/5 px-4 sm:px-10 py-6 sm:py-8 ${active ? 'group-battle-reveal' : ''}`}>
      {active && burstKey > 0 && <FlowerOverlay burstKey={burstKey} />}
      <div className="flex items-center justify-between gap-3 sm:gap-8">
        {/* Left: winner (or Group A when tied) */}
        <div className="flex-1 text-center">
          {!tied && <Trophy className="w-6 h-6 text-yellow-500 mx-auto mb-1" />}
          <div className={`text-base sm:text-2xl font-bold truncate ${!tied ? 'text-yellow-500' : theme.text}`}>{leftName}</div>
          <div className={`text-2xl sm:text-4xl font-black mt-1 ${!tied ? 'text-yellow-500' : theme.text}`}>{formatValue(leftCount)}</div>
          <div className={`text-xs mt-0.5 ${theme.textMuted}`}>{metricAbbr}</div>
        </div>

        {/* Center: verdict */}
        <div className="shrink-0 text-center px-2">
          <Trophy className={`w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 ${tied ? theme.textMuted : 'text-yellow-500'}`} />
          {tied ? (
            <div className={`text-sm sm:text-base font-semibold ${theme.text}`}>Tied</div>
          ) : (
            <div className={`text-sm sm:text-lg font-bold ${theme.text}`}>wins by {margin}{marginSuffix}</div>
          )}
        </div>

        {/* Right: loser (or Group B when tied) */}
        <div className="flex-1 text-center">
          <div className={`text-base sm:text-2xl font-bold truncate ${theme.text}`}>{rightName}</div>
          <div className={`text-2xl sm:text-4xl font-black mt-1 ${theme.text}`}>{formatValue(rightCount)}</div>
          <div className={`text-xs mt-0.5 ${theme.textMuted}`}>{metricAbbr}</div>
        </div>
      </div>
      <style>{`
        @keyframes groupBattleFadeIn {
          0% { opacity: 0; transform: translateY(-6px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .group-battle-reveal {
          animation: groupBattleFadeIn 400ms ease-out;
        }
      `}</style>
    </div>
  );
}

/**
 * StandingsTab Component
 *
 * Fully self-contained standings management:
 * - Loads and manages standings data
 * - Transforms API data to display format
 * - Handles recalculation of standings
 *
 * @param {Object} props
 * @param {Object} props.tournament - Tournament data
 * @param {string} props.activeTab - Currently active tournament detail tab (used to replay the group-battle reveal animation on each visit)
 * @param {Function} props.onTeamClick - Callback when a team is clicked
 * @param {boolean} props.canManageStandings - Whether user can manage standings
 * @returns {JSX.Element}
 */
export default function StandingsTab({
  tournament,
  activeTab,
  onTeamClick,
  canManageStandings = false
}) {
  const { theme } = useTheme();

  // Standings state
  const [apiStandings, setApiStandings] = useState([]);
  const [standingsLoading, setStandingsLoading] = useState(false);

  // Team groups state (e.g. MLP cross-group clusters) — used only to power
  // the group-vs-group winner banner below; unrelated to the per-team
  // "Group" column, which still comes from tournament_standings.group_name.
  const [apiTeamGroups, setApiTeamGroups] = useState([]);

  // Load standings from API
  useEffect(() => {
    const loadStandings = async () => {
      if (!tournament?.id) return;
      setStandingsLoading(true);
      try {
        const standings = await tournamentStandingsAPI.getByTournament(tournament.id);
        setApiStandings(standings || []);
      } catch (error) {
        console.error('Failed to load standings:', error);
      } finally {
        setStandingsLoading(false);
      }
    };
    loadStandings();
  }, [tournament?.id]);

  // Load team groups from API
  useEffect(() => {
    const loadTeamGroups = async () => {
      if (!tournament?.id) return;
      try {
        const groups = await tournamentTeamGroupsAPI.getByTournament(tournament.id);
        setApiTeamGroups(groups || []);
      } catch (error) {
        console.error('Failed to load team groups:', error);
      }
    };
    loadTeamGroups();
  }, [tournament?.id]);

  // Transform API standings to display format
  const standings = useMemo(() => {
    if (apiStandings.length > 0) {
      return apiStandings.map((s, idx) => ({
        rank: s.rank || idx + 1,
        team: s.participant_name || `${s.participant_type === 'team' ? 'Team' : 'Player'} ${s.participant_id}`,
        logo: s.participant_logo || (s.participant_type === 'team' ? '👥' : '👤'),
        wins: s.wins || 0,
        losses: s.losses || 0,
        draws: s.draws || 0,
        points: s.points || 0,
        matchesPlayed: s.matches_played || 0,
        pointsFor: s.stats?.points_for || 0,
        pointsAgainst: s.stats?.points_against || 0,
        groupName: s.group_name,
        participantId: s.participant_id
      }));
    }
    return [];
  }, [apiStandings]);

  const groupBattle = useMemo(
    () => computeGroupBattle(apiTeamGroups, standings),
    [apiTeamGroups, standings]
  );
  const showGroupBattle = tournament?.status === 'completed' && !!groupBattle;

  // Handler to recalculate standings from all completed matches
  const handleRecalculateStandings = async () => {
    if (!tournament?.id) return;

    try {
      setStandingsLoading(true);

      // Call the recalculate API
      await tournamentStandingsAPI.recalculate(tournament.id);

      // Reload standings
      const standings = await tournamentStandingsAPI.getByTournament(tournament.id);
      setApiStandings(standings || []);

      toast.success('Standings recalculated successfully!');
    } catch (error) {
      console.error('Failed to recalculate standings:', error);
      toast.error(error.message || 'Failed to recalculate standings');
    } finally {
      setStandingsLoading(false);
    }
  };

  if (standingsLoading) {
    return (
      <div className={`${theme.backgroundSecondary} rounded-lg overflow-hidden`}>
        <div className={`text-center py-12 text-sm ${theme.textMuted}`}>
          <div className={`w-8 h-8 border-2 ${theme.borderSecondary} border-t-transparent rounded-full animate-spin mx-auto mb-4`}></div>
          <p>Loading standings...</p>
        </div>
      </div>
    );
  }

  if (standings.length === 0) {
    return (
      <div className={`${theme.backgroundSecondary} rounded-lg overflow-hidden`}>
        <div className={`text-center py-12 text-sm ${theme.textMuted}`}>
          <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium mb-2">No standings available</p>
          <p className="mb-4">Standings will appear once the schedule is generated and matches are played</p>
          {canManageStandings && (
            <button
              onClick={handleRecalculateStandings}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-2 mx-auto`}
            >
              <RefreshCw className="w-4 h-4" />
              <span>Initialize Standings</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Recalculate Button */}
      {canManageStandings && (
        <div className="flex items-center justify-between">
          <h3 className={`text-lg font-bold ${theme.text}`}>Current Standings</h3>
          <button
            onClick={handleRecalculateStandings}
            className={`${btn.base} ${btn.sm} ${theme.buttonGhost} flex items-center gap-x-2`}
            title="Recalculate standings from all completed matches"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Recalculate</span>
          </button>
        </div>
      )}

      {/* Column legend */}
      <div className={`flex flex-wrap gap-x-4 gap-y-1 px-1 pb-1`}>
        {[
          { abbr: 'P',   label: 'Played' },
          { abbr: 'W',   label: 'Won' },
          { abbr: 'L',   label: 'Lost' },
          { abbr: 'D',   label: 'Drawn' },
          { abbr: 'F',   label: 'Points For (scored)' },
          { abbr: 'A',   label: 'Points Against (conceded)' },
          { abbr: 'F-A', label: 'Point Difference' },
          { abbr: 'PTS', label: 'Points' },
        ].map(({ abbr, label }) => (
          <span key={abbr} className={`text-xs ${theme.textMuted}`}>
            <span className="font-semibold">{abbr}</span> — {label}
          </span>
        ))}
      </div>

      {showGroupBattle && (
        <GroupBattleBanner
          groupBattle={groupBattle}
          active={activeTab === 'standings'}
          theme={theme}
        />
      )}

      <div className={`rounded-xl overflow-hidden shadow-lg border ${theme.table.border}`}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px]">
          <thead className={`${theme.table.headerBg} border-b-2 ${theme.table.border}`}>
            <tr>
              <th className={`px-3 sm:px-6 py-4 text-left text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle}`}>Rank</th>
              <th className={`px-3 sm:px-6 py-4 text-left text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle}`}>Team</th>
              {standings[0]?.groupName && (
                <th className={`px-3 sm:px-6 py-4 text-left text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle} hidden sm:table-cell`}>Group</th>
              )}
              <th className={`px-3 sm:px-6 py-4 text-center text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle}`}>P</th>
              <th className={`px-3 sm:px-6 py-4 text-center text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle}`}>W</th>
              <th className={`px-3 sm:px-6 py-4 text-center text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle}`}>L</th>
              <th className={`px-3 sm:px-6 py-4 text-center text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle} hidden sm:table-cell`}>D</th>
              <th className={`px-3 sm:px-6 py-4 text-center text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle} hidden md:table-cell`} title="Points Scored">F</th>
              <th className={`px-3 sm:px-6 py-4 text-center text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle} hidden md:table-cell`} title="Points Against">A</th>
              <th className={`px-3 sm:px-6 py-4 text-center text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle} hidden md:table-cell`} title="Point Difference">F-A</th>
              <th className={`px-3 sm:px-6 py-4 text-center text-xs sm:text-sm ${theme.table.headerText} ${theme.table.headerTextStyle}`}>PTS</th>
            </tr>
          </thead>
          <tbody className={theme.background}>
            {standings.map((team, index) => {
              // Special styling for top 3 positions
              const isTopThree = team.rank <= 3;
              const rankBadgeColor =
                team.rank === 1 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-yellow-900' :
                team.rank === 2 ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-900' :
                team.rank === 3 ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-amber-100' :
                'bg-gray-600 text-white border-2 border-gray-400';

              return (
              <tr
                key={`${team.rank}-${team.team}`}
                className={`border-b ${theme.table.border} ${
                  index % 2 === 0
                    ? theme.table.evenRowBg
                    : theme.table.oddRowBg
                }`}
              >
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${rankBadgeColor} shadow-sm`}>
                      {team.rank}
                    </span>
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-x-3">
                    <span className="text-xl">{team.logo}</span>
                    <div
                      className={`font-semibold text-sm ${theme.table.textColor} hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors`}
                      onClick={() => onTeamClick && onTeamClick(team.team, team.logo)}
                    >
                      {team.team}
                    </div>
                  </div>
                </td>
                {standings[0]?.groupName && (
                  <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${theme.table.textSecondary} hidden sm:table-cell`}>
                    {team.groupName || '-'}
                  </td>
                )}
                <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-center text-sm font-medium ${theme.table.textColor}`}>{team.matchesPlayed || 0}</td>
                <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-center text-sm font-semibold ${theme.table.winsColor}`}>{team.wins}</td>
                <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-center text-sm font-semibold ${theme.table.lossesColor}`}>{team.losses}</td>
                <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-center text-sm font-medium ${theme.table.drawsColor} hidden sm:table-cell`}>{team.draws || 0}</td>
                <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-center text-sm font-medium ${theme.table.textColor} hidden md:table-cell`}>{team.pointsFor || 0}</td>
                <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-center text-sm font-medium ${theme.table.textColor} hidden md:table-cell`}>{team.pointsAgainst || 0}</td>
                <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-center text-sm font-medium hidden md:table-cell ${
                  (team.pointsFor - team.pointsAgainst) > 0 ? 'text-green-400' :
                  (team.pointsFor - team.pointsAgainst) < 0 ? 'text-red-400' :
                  theme.table.textColor
                }`}>
                  {(team.pointsFor - team.pointsAgainst) > 0 ? '+' : ''}{(team.pointsFor || 0) - (team.pointsAgainst || 0)}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-center">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200">
                    {team.points}
                  </span>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}
